import { useQuery } from "@tanstack/react-query";
import { KeyRound, SlidersHorizontal } from "lucide-react";
import { useState } from "react";

import ScreenHeader from "../components/ScreenHeader";
import { errorMessageOf, isUserCancellation } from "../lib/auth";
import { formatBytes } from "../lib/media/format";
import { fetchMediaUsage, fetchStorageStatus } from "../lib/media/usage";
import { registerPasskey } from "../lib/passkey";
import { queryKeys } from "../lib/queryKeys";
import {
  ACCENT_CHOICES,
  DEFAULT_ACCENT,
  loadThemePrefs,
  saveThemePrefs,
  type ThemeMode,
  type ThemePrefs,
} from "../lib/theme";
import { useToast } from "../lib/toast";
import NavCustomizer from "./NavCustomizer";

const MODE_LABELS: { mode: ThemeMode; label: string }[] = [
  { mode: "light", label: "ライト" },
  { mode: "dark", label: "ダーク" },
  { mode: "system", label: "端末に合わせる" },
];

/** テーマカラーとダークモード。この端末だけの設定 */
function AppearanceSection() {
  const [prefs, setPrefs] = useState<ThemePrefs>(loadThemePrefs);
  const accent = prefs.accent ?? DEFAULT_ACCENT;
  const mode = prefs.mode ?? "system";

  function update(patch: ThemePrefs) {
    const next = { ...prefs, ...patch };
    setPrefs(next);
    saveThemePrefs(next);
  }

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xs text-[var(--color-sub)]">外観</h2>
      <div className="flex flex-col gap-4 rounded-xl border border-[var(--color-line)] bg-white p-3">
        <div className="flex flex-col gap-2">
          <span className="text-sm">テーマカラー</span>
          <div className="flex flex-wrap gap-2">
            {ACCENT_CHOICES.map(({ name, value }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  update({ accent: value });
                }}
                aria-label={`テーマカラー ${name}`}
                aria-pressed={accent === value}
                className={`size-9 rounded-full border-2 ${
                  accent === value ? "border-[var(--color-ink)]" : "border-transparent"
                }`}
                style={{ backgroundColor: value }}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <span className="text-sm">ダークモード</span>
          <div className="flex overflow-hidden rounded-lg border border-[var(--color-line)]">
            {MODE_LABELS.map(({ mode: value, label }) => (
              <button
                key={value}
                type="button"
                onClick={() => {
                  update({ mode: value });
                }}
                aria-pressed={mode === value}
                className={`flex-1 px-2 py-2 text-xs ${
                  mode === value
                    ? "bg-[var(--color-accent)] font-medium text-white"
                    : "text-[var(--color-sub)]"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
        <p className="text-xs text-[var(--color-sub)]">
          この端末だけに保存されます。テーマカラーはまずヘッダーの見出しに反映されます。
        </p>
      </div>
    </section>
  );
}

/** 使用量の帯。上限に近づいたら色で気づけるようにする */
function StorageMeter() {
  const status = useQuery({ queryKey: queryKeys.storageStatus, queryFn: fetchStorageStatus });
  const usage = useQuery({ queryKey: queryKeys.mediaUsage, queryFn: fetchMediaUsage });

  if (status.data === null || status.data === undefined) return null;
  const { usedBytes, limitBytes } = status.data;
  const ratio = limitBytes > 0 ? Math.min(usedBytes / limitBytes, 1) : 0;

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-xs text-[var(--color-sub)]">保存容量</h2>
      <div className="flex flex-col gap-2 rounded-xl border border-[var(--color-line)] bg-white p-3">
        <div className="flex items-baseline justify-between text-sm">
          <span>
            {formatBytes(usedBytes)} <span className="text-[var(--color-sub)]">使用中</span>
          </span>
          <span className="text-xs text-[var(--color-sub)]">上限 {formatBytes(limitBytes)}</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-[var(--color-bg)]">
          <div
            className="h-full rounded-full"
            style={{
              width: `${String(Math.max(ratio * 100, 1))}%`,
              backgroundColor: ratio >= 0.9 ? "var(--color-plum)" : "var(--color-teal)",
            }}
          />
        </div>
        {usage.data !== undefined && (
          <p className="text-xs text-[var(--color-sub)]">
            写真 {usage.data.photos}枚・動画 {usage.data.videos}本(ゴミ箱の分も含む)
          </p>
        )}
      </div>
    </section>
  );
}

/**
 * 設定。端末や認証にまつわるものだけを置く。
 * 名前・誕生日・連絡先はプロフィール画面、付き合った日・結婚記念日も
 * ふたりの項目としてプロフィール画面が受け持つ。
 */
export default function Settings() {
  const toast = useToast();
  const [registering, setRegistering] = useState(false);
  const [editingNav, setEditingNav] = useState(false);

  async function addPasskey() {
    setRegistering(true);
    try {
      await registerPasskey();
      toast("パスキーを登録しました。");
    } catch (e) {
      if (!isUserCancellation(e)) toast(errorMessageOf(e), "error");
    } finally {
      setRegistering(false);
    }
  }

  return (
    <>
      <ScreenHeader navKey="settings" />

      <div className="flex flex-col gap-6 p-4">
        <section className="flex flex-col gap-2">
          <h2 className="text-xs text-[var(--color-sub)]">メニュー</h2>
          <button
            type="button"
            onClick={() => {
              setEditingNav(true);
            }}
            className="flex items-center gap-2 rounded-xl border border-[var(--color-line)] bg-white p-3 text-left text-sm"
          >
            <SlidersHorizontal className="size-4 text-[var(--color-sub)]" aria-hidden />
            メニューを編集
          </button>
          <p className="text-xs text-[var(--color-sub)]">
            タブの並び・表示・名前・アイコンを自分用に変えられます。
          </p>
        </section>

        <AppearanceSection />

        <StorageMeter />

        <section className="flex flex-col gap-2">
          <h2 className="text-xs text-[var(--color-sub)]">パスキー</h2>
          <button
            type="button"
            onClick={() => void addPasskey()}
            disabled={registering}
            className="flex items-center gap-2 rounded-xl border border-[var(--color-line)] bg-white p-3 text-left text-sm disabled:opacity-50"
          >
            <KeyRound className="size-4 text-[var(--color-sub)]" aria-hidden />
            {registering ? "登録中..." : "この端末にパスキーを登録"}
          </button>
          <p className="text-xs text-[var(--color-sub)]">
            端末ごとに登録が必要です。使う端末それぞれで一度ずつ登録してください。
          </p>
        </section>
      </div>

      {editingNav && (
        <NavCustomizer
          onClose={() => {
            setEditingNav(false);
          }}
        />
      )}
    </>
  );
}
